import { useState, type FormEvent } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { useAuth } from '@/store/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { useSimulationSessions } from './hooks/useSimulationSessions'

export default function Simulation() {
  const owner = useAuth((s) => s.user?.role === 'OWNER')
  const { sessions, loading, pending, error, start, stop } = useSimulationSessions(owner)
  const [spot, setSpot] = useState('23000')
  const [volatility, setVolatility] = useState('16')
  const [seed, setSeed] = useState('42')
  const [confirmStop, setConfirmStop] = useState(false)
  if (!owner) return <Navigate to="/" replace />
  const active = sessions.find((session) => ['STARTING', 'RUNNING', 'STOPPING'].includes(session.status))
  const submit = (event: FormEvent) => {
    event.preventDefault()
    void start({ startSpot: Number(spot), baseVol: Number(volatility) / 100, seed: Number(seed) })
  }
  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="text-xl font-semibold">NIFTY simulation</h1>
          <p className="mt-2 text-sm text-muted-foreground">Run a shared practice session with synthetic option prices and OI. Traders choose their usual broker account; all fills stay in the simulation ledger.</p>
        </div>
        {error ? <p role="alert" className="rounded border border-destructive/40 p-3 text-sm text-destructive">{error}</p> : null}
        <section className="rounded-lg border border-border p-5" aria-label="Session controls">
          {loading ? <p role="status">Loading sessions…</p> : active ? (
            <div className="space-y-3">
              <p role="status" className="font-medium">Session {active.status.toLowerCase()}</p>
              <p className="break-all text-xs text-muted-foreground">{active.id}</p>
              <p className="text-sm">Starting spot {active.startSpot.toLocaleString('en-IN')} · IV {(active.baseVol * 100).toFixed(1)}% · Seed {active.seed}</p>
              <p className="text-sm text-muted-foreground">Stopping cancels pending simulated orders and closes simulated positions at the final bid/ask. Normal market data and account views return after settlement.</p>
              <div className="flex gap-3">
                <Button disabled={pending || active.status === 'STOPPING'} onClick={() => setConfirmStop(true)}>{active.status === 'STOPPING' ? 'Settling positions…' : 'Stop session'}</Button>
                <Button asChild variant="outline"><Link to="/">Open terminal</Link></Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="font-medium">Start a session</h2>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2"><Label htmlFor="simulation-spot">Starting NIFTY spot</Label><Input id="simulation-spot" type="number" min="1" max="1000000" step="0.05" required value={spot} onChange={(e) => setSpot(e.target.value)} /></div>
                <div className="space-y-2"><Label htmlFor="simulation-volatility">Base volatility (%)</Label><Input id="simulation-volatility" type="number" min="1" max="500" step="0.1" required value={volatility} onChange={(e) => setVolatility(e.target.value)} /></div>
                <div className="space-y-2"><Label htmlFor="simulation-seed">Scenario seed</Label><Input id="simulation-seed" type="number" min="0" max="2147483647" step="1" required value={seed} onChange={(e) => setSeed(e.target.value)} /></div>
              </div>
              <p className="text-xs text-muted-foreground">Uses contracts from the instrument catalogue. The seed selects the random price path. Pending normal-session orders must settle before starting.</p>
              <Button type="submit" disabled={pending}>{pending ? 'Starting…' : 'Start NIFTY simulation'}</Button>
            </form>
          )}
        </section>
        <section aria-label="Recent sessions" className="space-y-3">
          <h2 className="font-medium">Recent sessions</h2>
          {sessions.length === 0 && !loading ? <p className="text-sm text-muted-foreground">No simulation sessions yet.</p> : null}
          {sessions.map((session) => <div key={session.id} className="rounded border border-border p-3 text-sm">
            <span className="font-medium">{session.status}</span> · {new Date(session.createdAt).toLocaleString('en-IN')} · Spot {session.startSpot.toLocaleString('en-IN')}
            {session.failureReason ? <p className="mt-1 text-destructive">{session.failureReason}</p> : null}
          </div>)}
        </section>
      </div>
      <ConfirmDialog open={confirmStop} onOpenChange={setConfirmStop} title="Stop NIFTY simulation?" description="All pending simulated orders will be cancelled. Open simulated positions will be closed at the final synthetic bid/ask before the normal session returns." confirmLabel="Stop and settle" isPending={pending} pendingLabel="Stopping…" onConfirm={() => {
        if (active) void stop(active.id).then((ok) => { if (ok) setConfirmStop(false) })
      }} />
    </div>
  )
}
